/**
 * CreateWizard - 三步创建流程主页面
 * 左右分栏布局：左侧预览区 + 右侧表单区
 * 
 * 响应式设计：
 * - 桌面端：左右分栏（40/60）
 * - 移动端：单栏，预览区变为顶部折叠卡片
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useCreationStore } from '../../store/useCreationStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
import StepProgress from './components/StepProgress';
import PreviewPanel from './components/PreviewPanel';
import MobilePreviewCard from './components/MobilePreviewCard';
import Step1Character from './steps/Step1Character';
import Step2Blueprint from './steps/Step2Blueprint';
import Step3Visual from './steps/Step3Visual';
import NavigationButtons from './components/NavigationButtons';
import GuideCard from './components/GuideCard';

const stepTitles = {
  1: '角色设定',
  2: '故事蓝图',
  3: '视觉风格',
};

const stepDescriptions = {
  1: '创建绘本的主角形象',
  2: '配置故事的核心要素',
  3: '选择绘本的视觉呈现',
};

export default function CreateWizard() {
  const { step, setStep, setCharacter, setStory, setVisual, setFlow, flow } = useCreationStore();
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    const returnStep = searchParams.get('returnStep');
    const urlFlow = searchParams.get('flow');

    // 如果 URL 中有明确的 flow 参数（从首页进入时），优先使用，避免被数据库旧值覆盖
    if (urlFlow && (urlFlow === 'ai' || urlFlow === 'diy')) {
      const currentFlow = useCreationStore.getState().flow;
      if (urlFlow !== currentFlow) {
        setFlow(urlFlow as 'ai' | 'diy');
      }
    }

    if (storyId && !hasLoadedRef.current) {
      // 只加载一次，避免 saveDraft 写入 URL 后反复触发 loadStoryConfig 覆盖当前数据
      hasLoadedRef.current = true;
      loadStoryConfig(storyId, returnStep ? parseInt(returnStep, 10) : undefined);
    } else if (!storyId && returnStep) {
      // 没有 storyId 表示新建项目，但保留 localStorage 中的草稿数据（不要 reset）
      setStep(parseInt(returnStep, 10) as 1 | 2 | 3);
    }
  }, [storyId]);

  const loadStoryConfig = async (id: string, targetStep?: number) => {
    try {
      const token = localStorage.getItem('token');
      console.log(`[CreateWizard] Loading story config: ${id}`);
      const response = await fetch(`${API_BASE_URL}/stories/${id}/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      console.log('[CreateWizard] Config loaded:', result);

      if (result.success && result.data) {
        const story = result.data;
        // 安全解析 config（某些驱动可能返回字符串）
        const rawConfig = story.config;
        const config = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : (rawConfig || {});
        console.log('[CreateWizard] Parsed config:', config);

        // 优先保留本地已有数据，避免从数据库加载时覆盖用户当前编辑的内容
        // 在 fetch 完成后再次获取最新状态，避免异步期间用户操作被覆盖
        const current = useCreationStore.getState();
        const isCharacterEmpty = !current.character.name && current.character.traits.length === 0 && !current.character.appearance && !current.character.photoUrl;
        const isStoryEmpty = !current.story.theme && !current.story.outline && (current.story.pages === 6 || current.story.pages === 12) && current.story.age === '5-6';
        const isVisualEmpty = !current.visual.style;

        // 恢复创作模式（flow）
        const dbCreationMode = story.creationMode;
        const dbFlow = dbCreationMode === 'DIY_MANUAL' ? 'diy' : (dbCreationMode === 'AI_AUTO' ? 'ai' : undefined);
        // 防御性保护：
        // 1. 如果是从其他页面返回（如 DIY Editor），优先保留本地 flow
        // 2. 如果本地已有用户填写的数据（角色名称、性格、外貌、照片、角色类型、性别等），
        //    说明用户已经在当前流程中操作过，优先保留本地 flow，避免数据库旧值覆盖
        const isReturning = targetStep !== undefined;
        const hasUserData = !!current.character.name || current.character.traits.length > 0 || !!current.character.appearance || !!current.character.photoUrl || current.character.characterType !== 'human' || !!current.character.gender;
        // 如果 URL 中明确带了 flow 参数（用户从首页选择模式进入），完全尊重 URL，不覆盖
        const urlParams = new URLSearchParams(window.location.search);
        const urlFlow = urlParams.get('flow');
        if (dbFlow && dbFlow !== current.flow && !hasUserData && !isReturning && !urlFlow) {
          setFlow(dbFlow as 'ai' | 'diy');
        }

        if (isCharacterEmpty) {
          const characterData = typeof story.characterData === 'string' ? JSON.parse(story.characterData) : (story.characterData || {});
          setCharacter({
            name: config.characterName || '',
            traits: story.interests || [],
            appearance: config.appearance || '',
            photoUrl: story.photoUrl || '',
            identityCardUrl: characterData?.identityCardUrl || '',
            characterType: config.characterType || characterData?.characterType || 'human',
            gender: config.gender,
            species: config.species || characterData?.species || undefined,
          });
        } else {
          console.log('[CreateWizard] Local character data exists, skipping DB overwrite');
        }

        if (isStoryEmpty) {
          // 优先从 audioPreference 读取 speed（实际生效的值），其次从 config.speed
          const audioPref = story.audioPreference as any;
          const effectiveSpeed = audioPref?.speed !== undefined ? audioPref.speed : config.speed;
          setStory({
            age: story.targetAge || '5-6',
            pages: story.pageCount || 6,
            theme: story.theme || '',
            outline: config.outline || '',
            speed: effectiveSpeed,
          });
        } else {
          console.log('[CreateWizard] Local story data exists, skipping DB overwrite');
        }

        if (isVisualEmpty) {
          // 优先从 audioPreference 恢复 voiceId（实际生效的值），其次从 config.voiceId
          const audioPref = story.audioPreference as any;
          setVisual({
            style: story.artStyle || '',
            model: config.model || 'glm-5v-turbo',
            voice: audioPref?.voiceId || config.voiceId || 'xiaoyan',
          });
        } else {
          console.log('[CreateWizard] Local visual data exists, skipping DB overwrite');
        }

        // 如果从故事文案页返回，跳转到指定步骤（如视觉风格设计页 Step 3）
        if (targetStep && targetStep >= 1 && targetStep <= 3) {
          setStep(targetStep as 1 | 2 | 3);
        }
      } else {
        console.warn('[CreateWizard] Failed to load config:', result.error);
      }
    } catch (error) {
      console.error('[CreateWizard] 加载故事配置失败:', error);
    }
  };

  const handleStepClick = (targetStep: number) => {
    // 只能点击已完成的步骤或当前步骤
    if (targetStep <= step) {
      setStep(targetStep as 1 | 2 | 3);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
      {/* 顶部进度条 */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="w-full flex justify-center py-4">
          <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <StepProgress 
              currentStep={step} 
              onStepClick={handleStepClick}
            />
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="w-full flex justify-center py-8 lg:py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-7xl flex flex-col lg:flex-row justify-center gap-6 lg:gap-8 min-h-[calc(100vh-200px)]">
          
          {/* 左侧预览区 - 桌面端 */}
          <div className="hidden lg:block w-full max-w-[280px] flex-shrink-0">
            <div className="sticky top-28">
              <PreviewPanel />
            </div>
          </div>

          {/* 移动端预览卡片 - 可折叠 */}
          <div className="lg:hidden">
            <MobilePreviewCard 
              isExpanded={showMobilePreview}
              onToggle={() => setShowMobilePreview(!showMobilePreview)}
            />
          </div>

          {/* 右侧表单区 */}
          <div className="w-full max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full"
            >
              {/* 创作指南（仅 Step1 显示） */}
              {step === 1 && <GuideCard flow={flow} />}

              {/* 步骤标题 */}
              <div className="mb-8">
                <motion.h1 
                  key={step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-2xl lg:text-3xl font-bold text-slate-800 mb-2"
                >
                  Step {step}: {stepTitles[step]}
                </motion.h1>
                <motion.p 
                  key={`desc-${step}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-slate-500 text-sm lg:text-base"
                >
                  {stepDescriptions[step]}
                </motion.p>
              </div>

              {/* 步骤内容 */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 md:p-8">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      {step === 1 && <Step1Character />}
                      {step === 2 && <Step2Blueprint />}
                      {step === 3 && <Step3Visual />}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* 底部导航按钮 */}
                <div className="p-6 bg-slate-50/80 border-t border-slate-100">
                  <NavigationButtons />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
